import { supabase } from '@/integrations/supabase/client';

export interface ColumnMetadata {
  column_name: string;
  data_type: string;
  udt_name: string;
  is_nullable: string;
  column_default: string | null;
  is_identity: string;
}

export interface TableMetadata {
  table_name: string;
  columns: ColumnMetadata[];
  row_count: number;
}

export interface PaginatedResult<T = Record<string, unknown>> {
  rows: T[];
  total: number;
  page: number;
  pageSize: number;
}

interface GetTableRowsOpts {
  page: number;
  pageSize: number;
  search?: string;
  orderBy?: string;
  orderDir?: 'asc' | 'desc';
}

interface GetAuditLogOpts {
  page: number;
  pageSize: number;
  tableName?: string;
  adminUserId?: string;
}

async function invokeAdminCrud<T>(
  action: string,
  method: string,
  body?: Record<string, unknown>,
): Promise<T> {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new Error('Not authenticated');
  }

  const { data, error } = await supabase.functions.invoke('admin-crud', {
    headers: {
      Authorization: `Bearer ${session.access_token}`,
    },
    body: {
      action,
      method,
      ...body,
    },
  });

  if (error) {
    throw new Error(error.message ?? 'Admin API request failed');
  }

  if (data?.error) {
    throw new Error(data.error);
  }

  return data as T;
}

export async function getTableSchema(): Promise<TableMetadata[]> {
  const result = await invokeAdminCrud<{ tables: TableMetadata[] }>('schema', 'GET');
  return result.tables ?? [];
}

export async function getTableRows(
  tableName: string,
  opts: GetTableRowsOpts,
): Promise<PaginatedResult> {
  return invokeAdminCrud<PaginatedResult>('rows', 'GET', {
    tableName,
    page: opts.page,
    pageSize: opts.pageSize,
    search: opts.search,
    orderBy: opts.orderBy,
    orderDir: opts.orderDir,
  });
}

export async function getRow(tableName: string, rowId: string): Promise<Record<string, unknown>> {
  const result = await invokeAdminCrud<{ row: Record<string, unknown> }>('row', 'GET', {
    tableName,
    rowId,
  });
  return result.row;
}

export async function createRow(
  tableName: string,
  data: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const result = await invokeAdminCrud<{ row: Record<string, unknown> }>('create', 'POST', {
    tableName,
    data,
  });
  return result.row;
}

export async function updateRow(
  tableName: string,
  rowId: string,
  data: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const result = await invokeAdminCrud<{ row: Record<string, unknown> }>('update', 'PUT', {
    tableName,
    rowId,
    data,
  });
  return result.row;
}

export async function deleteRow(tableName: string, rowId: string): Promise<void> {
  await invokeAdminCrud<{ success: boolean }>('delete', 'DELETE', {
    tableName,
    rowId,
  });
}

export async function getAuditLog(opts: GetAuditLogOpts): Promise<PaginatedResult> {
  return invokeAdminCrud<PaginatedResult>('audit', 'GET', {
    page: opts.page,
    pageSize: opts.pageSize,
    tableName: opts.tableName,
    adminUserId: opts.adminUserId,
  });
}

export async function isSuperAdmin(): Promise<boolean> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return false;
    const { data } = await supabase
      .from('super_admins')
      .select('user_id')
      .eq('user_id', user.id)
      .maybeSingle();
    return !!data;
  } catch {
    return false;
  }
}
